// Setup › Finance › Clients & revenue (`/c/{slug}/setup/finance/clients`; 11 §Revenue
// attribution, M5-03 `clients.*`, `revenueEvents.*`): the clients a company serves with the seats
// that serve each (a share per seat; one seat's shares over every client sum to at most 1), and
// the revenue posted for a month. Revenue is append-only: a correction is a negative row.
// Managers read the clients; revenue is the owner's and finance admins' (a pay viewer reads their
// seat's); finance admins write. Calls on load: the standing, the clients and the month's revenue;
// the org chart for seat names.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  BriefcaseIcon,
  PencilIcon,
  PlusIcon,
  ReceiptTextIcon,
  Trash2Icon,
} from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { toMinor } from "../costs/costs-page";
import { formatDate, formatMoney } from "../format";
import { LoadError, Loading, LoadMore, Section } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import {
  type Currency,
  CurrencySelect,
  FinancePage,
  FormDialog,
  FormField,
  MonthSelect,
  monthKeyOf,
  monthLabel,
  monthsAround,
  ReadOnlyNotice,
  ratioText,
  useFinanceStanding,
  useOrgNames,
} from "./finance-parts";
import { clientsQuery, financeKeys, revenueQuery } from "./finance-queries";

type Client = Awaited<ReturnType<Api["client"]["clients"]["list"]>>["data"][number];
type RevenueKind = "invoice" | "subscription" | "manual";

const REVENUE_KIND_LABELS: Record<RevenueKind, string> = {
  invoice: "Invoice",
  subscription: "Subscription",
  manual: "Manual",
};

export function ClientsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const standing = useFinanceStanding(api, company);
  const current = monthKeyOf(new Date());
  const months = useMemo(() => monthsAround(current, 12, 0), [current]);
  const [month, setMonth] = useState(current);
  const clients = useInfiniteQuery({ ...clientsQuery(api, company.id), enabled: standing.loaded });
  const readsRevenue = standing.restricted || standing.compSeats.size > 0;
  const revenue = useInfiniteQuery({
    ...revenueQuery(api, company.id, month),
    enabled: readsRevenue,
  });
  const names = useOrgNames(api, company);
  const [editing, setEditing] = useState<Client | "new" | null>(null);
  const [posting, setPosting] = useState(false);
  const qc = useQueryClient();
  const archive = useMutation({
    mutationFn: (c: Client) =>
      api.client.clients.update({
        params: { co: company.id, id: c.id },
        body: { status: c.status === "active" ? "archived" : "active" },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.all(company.id) }),
  });
  const list = clients.data?.pages.flatMap((p) => p.data) ?? [];
  const rows = revenue.data?.pages.flatMap((p) => p.data) ?? [];
  const clientName = (id: string | null) =>
    id ? (list.find((c) => c.id === id)?.name ?? `Client …${id.slice(-4)}`) : "—";

  return (
    <FinancePage
      company={company}
      where="setup"
      title="Clients & revenue"
      icon={BriefcaseIcon}
      actions={
        standing.admin ? (
          <>
            <Button variant="ghost" size="sm" onClick={() => setPosting(true)}>
              <ReceiptTextIcon aria-hidden="true" />
              Post revenue
            </Button>
            <Button size="sm" onClick={() => setEditing("new")}>
              <PlusIcon aria-hidden="true" />
              Add a client
            </Button>
          </>
        ) : null
      }
    >
      {standing.query.isPending ? (
        <Loading label="Loading your finance access" rows={2} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {!standing.admin ? <ReadOnlyNotice company={company} owner={standing.owner} /> : null}
          {clients.isPending ? (
            <Loading label="Loading clients" rows={3} />
          ) : clients.isError ? (
            <LoadError what="Clients" error={clients.error} />
          ) : list.length === 0 ? (
            <EmptyState
              variant="card"
              icon={BriefcaseIcon}
              title="No clients yet"
              description="Add a client and the seats that serve it; its revenue then counts on those seats."
            />
          ) : (
            <>
              <Table data-slot="clients">
                <TableCaption>A seat's shares over every client sum to at most 100%.</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Client</TableHead>
                    <TableHead scope="col">Seats serving it</TableHead>
                    <TableHead scope="col">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((c) => (
                    <TableRow key={c.id} className="group/row" data-client={c.id}>
                      <TableHead scope="row" className="font-normal">
                        {c.name}
                        {c.status === "archived" ? (
                          <Badge variant="neutral" className="ml-2">
                            Archived
                          </Badge>
                        ) : null}
                      </TableHead>
                      <TableCell className="text-sm text-muted-foreground">
                        {c.seats.length
                          ? c.seats
                              .map((s) => `${names.seatName(s.seat_id)} ${ratioText(s.share)}`)
                              .join(" · ")
                          : "No seats"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {standing.admin ? (
                          <RowActions>
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit ${c.name}`}
                              tip="Edit"
                              onClick={() => setEditing(c)}
                            />
                            {c.status === "active" ? (
                              <ConfirmRowAction
                                icon={ArchiveIcon}
                                label={`Archive ${c.name}`}
                                tip="Archive"
                                disabled={archive.isPending}
                                title={`Archive “${c.name}”?`}
                                description="New revenue can no longer be posted to it; its history stays. Restore it any time."
                                confirmLabel="Archive"
                                onConfirm={() => archive.mutate(c)}
                              />
                            ) : (
                              <RowAction
                                icon={ArchiveRestoreIcon}
                                label={`Restore ${c.name}`}
                                tip="Restore"
                                disabled={archive.isPending}
                                onClick={() => archive.mutate(c)}
                              />
                            )}
                          </RowActions>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                what="clients"
                hasMore={clients.hasNextPage}
                loading={clients.isFetchingNextPage}
                onMore={() => void clients.fetchNextPage()}
              />
            </>
          )}
          <Section
            id="revenue"
            title="Revenue"
            description="Rows are never edited: post a negative row to correct one."
            actions={
              readsRevenue ? (
                <MonthSelect value={month} onChange={setMonth} months={months} />
              ) : null
            }
          >
            {!readsRevenue ? (
              <p className="text-sm text-muted-foreground">
                Revenue is shown to the owner and finance admins.
              </p>
            ) : revenue.isPending ? (
              <Loading label="Loading revenue" rows={3} />
            ) : revenue.isError ? (
              <LoadError what="Revenue" error={revenue.error} />
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">No revenue in {monthLabel(month)}.</p>
            ) : (
              <>
                <Table data-slot="revenue">
                  <TableCaption>{monthLabel(month)}, newest first.</TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">Date</TableHead>
                      <TableHead scope="col">Kind</TableHead>
                      <TableHead scope="col">Client or seat</TableHead>
                      <TableHead scope="col" className="max-md:hidden">
                        Reference
                      </TableHead>
                      <TableHead scope="col" className="text-right">
                        Amount
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.id} data-revenue={r.id}>
                        <TableCell className="text-muted-foreground tabular-nums">
                          {formatDate(r.occurred_at)}
                        </TableCell>
                        <TableHead scope="row" className="font-normal">
                          {REVENUE_KIND_LABELS[r.kind]}
                        </TableHead>
                        <TableCell>
                          {r.seat_id ? names.seatName(r.seat_id) : clientName(r.client_id)}
                        </TableCell>
                        <TableCell className="text-muted-foreground max-md:hidden">
                          {r.source_ref
                            ? `${r.source_ref.system} ${r.source_ref.id}`
                            : (r.note ?? "—")}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(r.amount_minor, r.currency)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <LoadMore
                  what="revenue"
                  hasMore={revenue.hasNextPage}
                  loading={revenue.isFetchingNextPage}
                  onMore={() => void revenue.fetchNextPage()}
                />
              </>
            )}
          </Section>
        </div>
      )}
      {editing ? (
        <ClientDialog
          api={api}
          company={company}
          row={editing === "new" ? null : editing}
          seats={names.seats}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {posting ? (
        <RevenueDialog
          api={api}
          company={company}
          clients={list.filter((c) => c.status === "active")}
          seats={names.seats}
          onClose={() => setPosting(false)}
        />
      ) : null}
    </FinancePage>
  );
}

/** "50" (percent) → "0.5"; null when not a percent above 0 and at most 100 with ≤ 2 decimals. */
export function shareOf(percent: string): string | null {
  const v = percent.trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(v)) return null;
  const n = Number(v);
  if (n <= 0 || n > 100) return null;
  return String(Math.round(n * 100) / 10_000);
}

function ClientDialog({
  api,
  company,
  row,
  seats,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  row: Client | null;
  seats: { id: string; title: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(row?.name ?? "");
  const [shares, setShares] = useState<{ seat: string; percent: string }[]>(
    row?.seats.map((s) => ({ seat: s.seat_id, percent: String(Number(s.share) * 100) })) ?? [],
  );
  const ids = { name: useId(), shares: useId() };
  const parsed = shares.map((s) => ({ seat_id: s.seat, share: shareOf(s.percent) }));
  const bad = parsed.some((s) => s.share === null || !s.seat_id);
  const body = {
    name: name.trim(),
    seats: parsed.map((s) => ({ seat_id: s.seat_id, share: s.share ?? "0" })),
  };
  const save = useMutation({
    mutationFn: () =>
      row
        ? api.client.clients.update({ params: { co: company.id, id: row.id }, body })
        : api.client.clients.create({
            params: { co: company.id },
            idempotencyKey: newIdempotencyKey(),
            body,
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={row ? "Edit client" : "Add a client"}
      description="Its revenue that names no seat counts on these seats by their share."
      submitLabel={row ? "Save" : "Add client"}
      pending={save.isPending}
      error={save.error}
      canSubmit={name.trim().length > 0 && !bad}
      onSubmit={() => save.mutate()}
    >
      <FormField id={ids.name} label="Name">
        <Input
          id={ids.name}
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </FormField>
      <fieldset className="flex flex-col gap-2" data-slot="client-seats">
        <legend className="mb-1 text-sm font-medium">Seats serving it</legend>
        {shares.map((s, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional
          <div key={i} className="flex flex-wrap items-center gap-2">
            <NativeSelect
              aria-label={`Seat ${i + 1}`}
              value={s.seat}
              onChange={(e) =>
                setShares((l) => l.map((x, j) => (j === i ? { ...x, seat: e.target.value } : x)))
              }
            >
              {seats.map((seat) => (
                <NativeSelectOption key={seat.id} value={seat.id}>
                  {seat.title}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Input
              aria-label={`Share of seat ${i + 1} (%)`}
              inputMode="decimal"
              className="w-24"
              value={s.percent}
              onChange={(e) =>
                setShares((l) => l.map((x, j) => (j === i ? { ...x, percent: e.target.value } : x)))
              }
            />
            <span className="text-sm text-muted-foreground">%</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={`Remove seat ${i + 1}`}
              onClick={() => setShares((l) => l.filter((_, j) => j !== i))}
            >
              <Trash2Icon aria-hidden="true" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          disabled={!seats.length}
          onClick={() => setShares((l) => [...l, { seat: seats[0]?.id ?? "", percent: "100" }])}
        >
          <PlusIcon aria-hidden="true" />
          Add a seat
        </Button>
        {bad ? (
          <p className="text-xs text-muted-foreground">Each share is above 0% and at most 100%.</p>
        ) : null}
      </fieldset>
    </FormDialog>
  );
}

function RevenueDialog({
  api,
  company,
  clients,
  seats,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  clients: Client[];
  seats: { id: string; title: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<RevenueKind>("invoice");
  const [to, setTo] = useState(
    clients[0] ? `client:${clients[0].id}` : seats[0] ? `seat:${seats[0].id}` : "",
  );
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<Currency>("USD");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const ids = {
    kind: useId(),
    to: useId(),
    amount: useId(),
    cur: useId(),
    date: useId(),
    ref: useId(),
    note: useId(),
  };
  const minor = toMinor(amount, currency);
  const post = useMutation({
    mutationFn: () =>
      api.client.revenueEvents.create({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          // Midday of the chosen day, never ahead of now.
          occurred_at: new Date(
            Math.min(Date.parse(`${date}T12:00:00.000Z`), Date.now()),
          ).toISOString(),
          amount_minor: minor ?? 0,
          currency,
          kind,
          client_id: to.startsWith("client:") ? to.slice(7) : null,
          seat_id: to.startsWith("seat:") ? to.slice(5) : null,
          source_ref: reference.trim() ? { system: kind, id: reference.trim() } : null,
          note: note.trim() || null,
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="Post revenue"
      description="A row is never edited: post a negative amount to correct one."
      submitLabel="Post revenue"
      pending={post.isPending}
      error={post.error}
      canSubmit={minor !== null && minor !== 0 && Boolean(date)}
      onSubmit={() => post.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.kind} label="Kind">
          <NativeSelect
            id={ids.kind}
            value={kind}
            onChange={(e) => setKind(e.target.value as RevenueKind)}
          >
            {(Object.keys(REVENUE_KIND_LABELS) as RevenueKind[]).map((k) => (
              <NativeSelectOption key={k} value={k}>
                {REVENUE_KIND_LABELS[k]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.to} label="From">
          <NativeSelect id={ids.to} value={to} onChange={(e) => setTo(e.target.value)}>
            <NativeSelectOption value="">The company (no client or seat)</NativeSelectOption>
            {clients.map((c) => (
              <NativeSelectOption key={c.id} value={`client:${c.id}`}>
                Client: {c.name}
              </NativeSelectOption>
            ))}
            {seats.map((s) => (
              <NativeSelectOption key={s.id} value={`seat:${s.id}`}>
                Seat: {s.title}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        <FormField
          id={ids.amount}
          label="Amount"
          error={
            amount !== "" && (minor === null || minor === 0)
              ? "Enter a non-zero amount like 4800.00."
              : null
          }
        >
          <Input
            id={ids.amount}
            inputMode="decimal"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </FormField>
        <FormField id={ids.cur} label="Currency">
          <CurrencySelect id={ids.cur} value={currency} onChange={setCurrency} />
        </FormField>
        <FormField id={ids.date} label="Date">
          <Input
            id={ids.date}
            type="date"
            required
            max={new Date().toISOString().slice(0, 10)}
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </FormField>
        <FormField id={ids.ref} label="Reference" hint="An invoice number (optional).">
          <Input
            id={ids.ref}
            maxLength={200}
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </FormField>
      </div>
      <FormField id={ids.note} label="Note">
        <Input
          id={ids.note}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormField>
    </FormDialog>
  );
}
